import { ChevronRight, ExpandMore } from "@mui/icons-material";
import { Alert, Box, Divider, LinearProgress, Tooltip, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import ClaimApiService from "../../adapters/xhr/ClaimApiService";
import { MyButton } from "../../components/my-button/MyButton";
import { MyDatePicker } from "../../components/my-date-picker/MyDatePicker";
import { ApproveClaimJobStatus } from "../../Constant";
import SnackbarUtils from "../../SnackbarUtils";
import { formatDate } from "../../Utils";

const LinearProgressWithLabel = (props) => {
  return (
    <Box display="flex" alignItems="center">
      <Box width="100%" mr={1}>
        <LinearProgress variant="determinate" {...props} />
      </Box>
      <Box minWidth={35}>
        <Typography variant="body2" color="textSecondary">{`${Math.round(props.value)}%`}</Typography>
      </Box>
    </Box>
  );
};

export const TrainApprovalJobPage = () => {
  const { t, i18n } = useTranslation();
  const [openClaimsToBeRejected, setOpenClaimsToBeRejected] = useState(false);
  const [openClaimsToBeApproved, setOpenClaimsToBeApproved] = useState(false);
  const [openClaimsToBeManualOpened, setOpenClaimsToBeManualOpened] = useState(false);
  const [totalClaims, setTotalClaims] = useState(-1);
  const [currentJob, setCurrentJob] = useState();
  const [startDate, setStartDate] = useState();
  const [startDateErr, setStartDateErr] = useState(false);
  const [endDate, setEndDate] = useState();
  const [endDateErr, setEndDateErr] = useState(false);
  const [claimsForApprove, setClaimsForApprove] = useState();

  const getCurrentApproveClaimJob = async () => {
    let response = await ClaimApiService.getCurrentApproveClaimJob();
    if (!!response.data) response.data.statusTxt = getApproveClaimJobStatus(response.data.status);
    setCurrentJob(response.data);
  };

  const getApproveClaimJobStatus = (id) => {
    switch (id) {
      case ApproveClaimJobStatus.Default:
        return t("default");
      case ApproveClaimJobStatus.Initialized:
        return t("initialized");
      case ApproveClaimJobStatus.Running:
        return t("running");
      case ApproveClaimJobStatus.Completed:
        return t("completed");
      case ApproveClaimJobStatus.Error:
        return t("error");
    }
  };

  const getApproveClaimJobById = async () => {
    let response = await ClaimApiService.getApproveClaimJobById(currentJob.id);
    if (!!response.data) response.data.statusTxt = getApproveClaimJobStatus(response.data.status);
    setCurrentJob(response.data);
  };

  const approveClaims = async () => {
    setStartDateErr(!startDate);
    setEndDateErr(!endDate);
    if (!startDate || !endDate) return;
    let payload = {};
    if (startDate) {
      payload.startDate = formatDate(startDate);
    }
    if (endDate) {
      payload.endDate = formatDate(endDate);
    }
    let result = await ClaimApiService.approveClaims(payload);
    setCurrentJob({ id: result.data, progress: 0 });
  };

  const getClaimsForApprove = async () => {
    setCurrentJob();
    setStartDateErr(!startDate);
    setEndDateErr(!endDate);
    if (!startDate || !endDate) return;
    let payload = {};
    if (startDate) {
      payload.startDate = formatDate(startDate);
    }
    if (endDate) {
      payload.endDate = formatDate(endDate);
    }
    let result = await ClaimApiService.getClaimsForApprove(payload);
    if (result.isSuccess) {
      setClaimsForApprove(result.data);
      setTotalClaims(result.data?.totalClaims);
    } else {
      SnackbarUtils.error(t("error"));
    }
  };

  const getApproveClaims = async () => {
    if (!!startDate && !!endDate) {
      setStartDateErr(false);
      setEndDateErr(false);
      let payload = {
        startDate: formatDate(startDate),
        endDate: formatDate(endDate),
      };
      let result = await ClaimApiService.getApproveClaims(payload);
      if (result.data) {
        setTotalClaims(result.data.totalClaims);
      }
    }
  };

  useEffect(() => {
    getCurrentApproveClaimJob();
  }, []);

  useEffect(() => {
    setCurrentJob();
    setClaimsForApprove();
    getApproveClaims();
  }, [startDate, endDate]);

  useEffect(() => {
    if (currentJob && currentJob.id) {
      if (!currentJob.status) {
        getApproveClaimJobById();
      } else if (currentJob.status !== ApproveClaimJobStatus.Completed && currentJob.status !== ApproveClaimJobStatus.Error) {
        const myTimeout = setTimeout(() => getApproveClaimJobById(), 5000);
        return () => {
          clearTimeout(myTimeout);
        };
      }
    }
  }, [currentJob]);

  return (
    <>
      <Box sx={{ display: "flex" }}>
        <MyDatePicker error={startDateErr} size="small" onAccept={(date) => setStartDate(date.toString())} value={startDate} label={t("from")} style={{ width: "12rem", margin: "1rem 0 1rem 1rem" }} />
        <MyDatePicker error={endDateErr} size="small" onAccept={(date) => setEndDate(date.toString())} value={endDate} label={t("to")} style={{ width: "12rem", margin: "1rem 0 1rem 1rem" }} />
        <MyButton onClick={getClaimsForApprove} disabled={totalClaims <= 0 || (currentJob && currentJob.status !== ApproveClaimJobStatus.Completed && currentJob.status !== ApproveClaimJobStatus.Error)} text={t("search")} color="primary" style={{ width: "7rem", margin: "1rem 0 1rem 1rem" }} />
        <MyButton onClick={approveClaims} disabled={totalClaims <= 0 || (currentJob && currentJob.status !== ApproveClaimJobStatus.Completed && currentJob.status !== ApproveClaimJobStatus.Error)} text={t("run")} color="primary" style={{ width: "7rem", margin: "1rem 0 1rem 1rem" }} />
      </Box>
      <Divider />
      <div className="padding left-1 top-1 right-1">
        {totalClaims >= 0 ? (
          <p>
            {t("number_of_claims_to_be_approved")} - {totalClaims}
          </p>
        ) : (
          <></>
        )}
        {claimsForApprove && (
          <>
            <p>
              <strong>{t("search_result")}</strong>
            </p>
            <p>
              {t("total_claims")} - {claimsForApprove?.totalClaims}
            </p>
            {claimsForApprove?.totalClaimsToBeApproved > 0 ? (
              <p className="selector" style={{ display: "flex", alignItems: "center", width: "fit-content" }} onClick={(e) => setOpenClaimsToBeApproved(!openClaimsToBeApproved)}>
                {!openClaimsToBeApproved ? <ChevronRight fontSize="small" /> : <ExpandMore fontSize="small" />}
                {t("total_approved")} - {claimsForApprove?.totalClaimsToBeApproved}
              </p>
            ) : (
              <p>
                {t("total_approved")} - {claimsForApprove?.totalClaimsToBeApproved}
              </p>
            )}
            {openClaimsToBeApproved && (
              <p style={{ display: "flex", flexWrap: "wrap", marginLeft: "1rem" }}>
                {claimsForApprove?.claimsToBeApproved.map((claim) =>
                  claim.ticketNumber ? (
                    <span key={claim.id} className="margin bottom-1" style={{ textDecoration: "underline", flex: "0 0 50%" }}>
                      {claimsForApprove.claimsToBeApproved.some((item) => item.ticketNumber === claim.ticketNumber && item.id !== claim.id) ? claim.ticketNumber + "(" + claim.id + ")" : claim.ticketNumber}
                    </span>
                  ) : (
                    <span className="margin bottom-1" style={{ textDecoration: "underline", flex: "0 0 50%" }}>
                      {claim.id}
                    </span>
                  )
                )}
              </p>
            )}
            {claimsForApprove?.totalClaimsToBeRejected > 0 ? (
              <p className="selector" style={{ display: "flex", alignItems: "center", width: "fit-content" }} onClick={(e) => setOpenClaimsToBeRejected(!openClaimsToBeRejected)}>
                {!openClaimsToBeRejected ? <ChevronRight fontSize="small" /> : <ExpandMore fontSize="small" />}
                {t("total_rejected")} - {claimsForApprove?.totalClaimsToBeRejected}
              </p>
            ) : (
              <p>
                {t("total_rejected")} - {claimsForApprove?.totalClaimsToBeRejected}
              </p>
            )}
            {openClaimsToBeRejected && (
              <p style={{ display: "flex", flexWrap: "wrap", marginLeft: "1rem" }}>
                {claimsForApprove?.claimsToBeRejected.map((claim) =>
                  claim.ticketNumber ? (
                    <Tooltip
                      key={claim.id}
                      title={claim.reason
                        ?.split(";")
                        .map((item) => t(item))
                        .join(" ")}
                      placement="top-start"
                    >
                      <span className="margin bottom-1" style={{ textDecoration: "underline", flex: "0 0 50%" }}>
                        {claimsForApprove.claimsToBeRejected.some((item) => item.ticketNumber === claim.ticketNumber && item.id !== claim.id) ? claim.ticketNumber + "(" + claim.id + ")" : claim.ticketNumber}
                      </span>
                    </Tooltip>
                  ) : (
                    <span className="margin bottom-1" style={{ textDecoration: "underline", flex: "0 0 50%" }}>
                      {claim.id}
                    </span>
                  )
                )}
              </p>
            )}
            {claimsForApprove?.totalClaimsToBeManualOpened > 0 ? (
              <p className="selector" style={{ display: "flex", alignItems: "center", width: "fit-content" }} onClick={(e) => setOpenClaimsToBeManualOpened(!openClaimsToBeManualOpened)}>
                {!openClaimsToBeManualOpened ? <ChevronRight fontSize="small" /> : <ExpandMore fontSize="small" />}
                {t("total_manual_opened")} - {claimsForApprove?.totalClaimsToBeManualOpened}
              </p>
            ) : (
              <p>
                {t("total_manual_opened")} - {claimsForApprove?.totalClaimsToBeManualOpened}
              </p>
            )}
            {openClaimsToBeManualOpened && (
              <p style={{ display: "flex", flexWrap: "wrap", marginLeft: "1rem" }}>
                {claimsForApprove?.claimsToBeManualOpened.map((claim) =>
                  claim.ticketNumber ? (
                    <span key={claim.id} className="margin bottom-1" style={{ textDecoration: "underline", flex: "0 0 50%" }}>
                      {claimsForApprove.claimsToBeManualOpened.some((item) => item.ticketNumber === claim.ticketNumber && item.id !== claim.id) ? claim.ticketNumber + "(" + claim.id + ")" : claim.ticketNumber}
                    </span>
                  ) : (
                    <span className="margin bottom-1" style={{ textDecoration: "underline", flex: "0 0 50%" }}>
                      {claim.id}
                    </span>
                  )
                )}
              </p>
            )}
          </>
        )}
        {currentJob && (
          <>
            <p>
              <strong>{t("job_progress")}</strong>
            </p>
            <p>
              {t("status")} - {currentJob?.statusTxt}
            </p>
            <p>
              {t("total_claims")} - {currentJob?.totalClaims}
            </p>
            <p>
              {t("total_approved")} - {currentJob?.totalApproved}
            </p>
            <p>
              {t("total_rejected")} - {currentJob?.totalRejected}
            </p>
            <p>
              {t("total_manual_opened")} - {currentJob?.totalManualOpened}
            </p>
            <LinearProgressWithLabel value={currentJob?.progress} />
            {(currentJob?.status === ApproveClaimJobStatus.Completed || currentJob?.status === ApproveClaimJobStatus.Error) && (
              <Alert severity="info" className="margin top-1 bottom-1">
                {t("job_completed")}
              </Alert>
            )}
          </>
        )}
      </div>
    </>
  );
};
